"use client";

import { Bot, Ear, FileCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Approach() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    icon: Bot,
    title: "Initial Consultation",
    description: "We begin by understanding your unique business challenges and defining the core objectives for our partnership.",
    gradient: "from-blue-100 to-blue-200",
    offset: false
  }, {
    icon: Ear,
    title: "Strategic Planning",
    description: "Our team crafts a tailored roadmap, aligning our expertise with your specific goals to ensure measurable success.",
    gradient: "from-blue-200 to-blue-300",
    offset: true
  }, {
    icon: FileCheck,
    title: "Execution & Growth",
    description: "We implement the strategy with precision, providing ongoing support and optimization to drive sustainable results.",
    gradient: "from-blue-300 to-blue-400",
    offset: false
  }];
  return <section data-section-id="18071" id="our-approach" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-50">
        <div className="absolute top-1/4 left-1/4 h-[500px] w-[500px] rounded-lg bg-blue-100 blur-3xl opacity-40" />
        <div className="absolute bottom-1/4 right-1/4 h-[500px] w-[500px] rounded-lg bg-blue-200 blur-3xl opacity-40" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-10">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-default text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground mb-5">
            Our Methodology
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[3.5rem] font-medium leading-[1.04] tracking-tight text-foreground">
            From vision to{" "}
            <span className="italic text-primary">impact</span> in three
            phases.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
            We prioritize clarity and collaboration, ensuring every step of our process is transparent and focused on your long-term success.
          </p>
        </div>

        {}
        <div ref={grid.ref} className={`relative mt-20 md:mt-24 transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100" : "opacity-0"}`}>
          {}
          <svg aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-[88px] hidden md:block w-full h-24" viewBox="0 0 1200 100" preserveAspectRatio="none" fill="none">
            <defs>
              <linearGradient id="flow-connector" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(199 89% 48%)" stopOpacity="0.3" />
                <stop offset="50%" stopColor="hsl(217 91% 60%)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="hsl(199 89% 0%)" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            <path d="M 200 20 Q 450 90, 600 50 T 1000 20" stroke="url(#flow-connector)" strokeWidth="1" fill="none" />
          </svg>

          {}
          <div aria-hidden="true" className="pointer-events-none absolute left-[44px] top-16 bottom-16 w-px md:hidden" style={{
          background: "linear-gradient(to bottom, hsl(199 89% 48% / 0.3), hsl(217 91% 60% / 0.3), hsl(199 89% 0% / 0.3))"
        }} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${step.offset ? "md:mt-6" : ""}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  <Card className="group relative h-full border border-border bg-card backdrop-blur-xl rounded-lg p-7 md:p-9 shadow-lg transition-all duration-[350ms] ease-out hover:-translate-y-1 hover:shadow-2xl">
                    {}
                    <div className="flex items-center gap-4 mb-5">
                      <div className={`relative flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br ${step.gradient} opacity-60`}>
                        <Icon className="absolute h-6 w-6 text-foreground" strokeWidth={1.5} />
                      </div>
                      <span className="font-mono text-xs tracking-[0.04em] text-muted-foreground">
                        {`0${i + 1}`}
                      </span>
                    </div>

                    <h3 className="font-default text-2xl font-medium tracking-tight text-foreground mb-3">
                      {step.title}
                    </h3>
                    <p className="text-base leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}